import { parseIsoDate } from "./date.ts";
import { previewHistoricalImpact, type HistoricalImpactPreview } from "./historical-impact.ts";
import {
  archiveStarting,
  restoreStarting,
  selectCategoryArchiveOperationIds,
  selectCategoryRestoreOperationIds,
} from "./lifecycle.ts";
import { DomainError, type IsoDate, type MatrixDocument } from "./model.ts";

export type OwnerMaintenanceCommand =
  | Readonly<{ kind: "rename-operation"; operationId: string; name: string }>
  | Readonly<{ kind: "rename-category"; categoryId: string; name: string }>
  | Readonly<{ kind: "move-operation"; operationId: string; categoryId: string }>
  | Readonly<{ kind: "archive-operation"; operationId: string; effectiveFrom: IsoDate }>
  | Readonly<{ kind: "restore-operation"; operationId: string; effectiveFrom: IsoDate }>
  | Readonly<{ kind: "archive-category"; categoryId: string; effectiveFrom: IsoDate }>
  | Readonly<{
    kind: "restore-category";
    categoryId: string;
    effectiveFrom: IsoDate;
    cascadeOperationIds: readonly string[];
    deselectedOperationIds?: readonly string[];
  }>
  | Readonly<{ kind: "delete-operation"; operationId: string }>
  | Readonly<{ kind: "delete-category"; categoryId: string }>;

export interface MaintenanceCounts {
  readonly affectedCategories: number;
  readonly affectedOperations: number;
  readonly affectedEntries: number;
  readonly removedCategories: number;
  readonly removedOperations: number;
  readonly removedEntries: number;
}

export interface CategoryHistoricalImpact {
  readonly categoryId: string;
  readonly impact: HistoricalImpactPreview;
}

export interface OwnerMaintenancePreview {
  readonly command: OwnerMaintenanceCommand;
  readonly through: IsoDate;
  readonly affectedCategoryIds: readonly string[];
  readonly affectedOperationIds: readonly string[];
  readonly counts: MaintenanceCounts;
  readonly impact: HistoricalImpactPreview;
  readonly categoryImpacts: readonly CategoryHistoricalImpact[];
  readonly beforeFingerprint: string;
  readonly afterFingerprint: string;
  readonly deleteSafeguard: null | Readonly<{
    backupRequired: true;
    confirmationText: string;
  }>;
}

export interface DeleteAuthorization {
  readonly backupDownloaded: boolean;
  readonly confirmationText: string;
}

type Proposal = {
  document: MatrixDocument;
  affectedCategoryIds: string[];
  affectedOperationIds: string[];
  affectedEntryOperationIds: string[];
  effectiveFrom?: IsoDate;
  deleteName?: string;
  removedCategories: number;
  removedOperations: number;
  removedEntries: number;
};

function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "undefined";
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const record = value as Readonly<Record<string, unknown>>;
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(",")}}`;
}

export function maintenanceDocumentFingerprint(document: MatrixDocument): string {
  return `maintenance-document:${canonicalJson(document)}`;
}

function requiredText(value: string, max: number, code: string): string {
  const trimmed = value.trim();
  if (trimmed.length < 1 || trimmed.length > max) throw new DomainError(code, `name must contain 1 to ${max} characters`);
  return trimmed;
}

function operationIndex(document: MatrixDocument, operationId: string): number {
  const index = document.operations.findIndex((operation) => operation.id === operationId);
  if (index < 0) throw new DomainError("UNKNOWN_OPERATION", `Unknown operation ${operationId}`);
  return index;
}

function categoryIndex(document: MatrixDocument, categoryId: string): number {
  const index = document.categories.findIndex((category) => category.id === categoryId);
  if (index < 0) throw new DomainError("UNKNOWN_CATEGORY", `Unknown category ${categoryId}`);
  return index;
}

function uniqueIds(ids: readonly string[], code: string): string[] {
  const unique = [...new Set(ids)];
  if (unique.length !== ids.length) throw new DomainError(code, "identifiers must be unique");
  return unique;
}

function sameDocument(left: MatrixDocument, right: MatrixDocument): boolean {
  return maintenanceDocumentFingerprint(left) === maintenanceDocumentFingerprint(right);
}

function proposeMaintenance(document: MatrixDocument, command: OwnerMaintenanceCommand): Proposal {
  if (command.kind === "rename-operation") {
    const index = operationIndex(document, command.operationId);
    const operation = document.operations[index]!;
    const name = requiredText(command.name, 300, "INVALID_OPERATION_NAME");
    if (operation.name === name) throw new DomainError("NO_MAINTENANCE_CHANGE", "operation already has this name");
    const next = { ...document, operations: document.operations.map((item, itemIndex) => itemIndex === index ? { ...item, name } : item) };
    return proposal(next, [operation.categoryId], [operation.id], [operation.id]);
  }
  if (command.kind === "rename-category") {
    const index = categoryIndex(document, command.categoryId);
    const category = document.categories[index]!;
    const name = requiredText(command.name, 200, "INVALID_CATEGORY_NAME");
    if (category.name === name) throw new DomainError("NO_MAINTENANCE_CHANGE", "category already has this name");
    const operationIds = document.operations.filter((operation) => operation.categoryId === category.id).map((operation) => operation.id);
    const next = { ...document, categories: document.categories.map((item, itemIndex) => itemIndex === index ? { ...item, name } : item) };
    return proposal(next, [category.id], operationIds, operationIds);
  }
  if (command.kind === "move-operation") {
    const index = operationIndex(document, command.operationId);
    categoryIndex(document, command.categoryId);
    const operation = document.operations[index]!;
    if (operation.categoryId === command.categoryId) throw new DomainError("NO_MAINTENANCE_CHANGE", "operation already belongs to this category");
    const categories = document.categories.map((category) => ({
      ...category,
      operationOrder: category.id === operation.categoryId
        ? category.operationOrder.filter((id) => id !== operation.id)
        : category.id === command.categoryId
        ? [...category.operationOrder.filter((id) => id !== operation.id), operation.id]
        : category.operationOrder,
    }));
    const operations = document.operations.map((item, itemIndex) => itemIndex === index ? { ...item, categoryId: command.categoryId } : item);
    return proposal({ ...document, categories, operations }, [operation.categoryId, command.categoryId], [operation.id], [operation.id]);
  }
  if (command.kind === "archive-operation" || command.kind === "restore-operation") {
    parseIsoDate(command.effectiveFrom);
    const index = operationIndex(document, command.operationId);
    const operation = document.operations[index]!;
    const changed = command.kind === "archive-operation"
      ? archiveStarting(operation, command.effectiveFrom)
      : restoreStarting(operation, command.effectiveFrom);
    const next = { ...document, operations: document.operations.map((item, itemIndex) => itemIndex === index ? changed : item) };
    if (sameDocument(document, next)) throw new DomainError("NO_MAINTENANCE_CHANGE", "lifecycle is unchanged");
    return proposal(next, [operation.categoryId], [operation.id], [operation.id], command.effectiveFrom);
  }
  if (command.kind === "archive-category") {
    parseIsoDate(command.effectiveFrom);
    const index = categoryIndex(document, command.categoryId);
    const category = document.categories[index]!;
    const operationIds = selectCategoryArchiveOperationIds(category, document.operations, command.effectiveFrom);
    const operationSet = new Set(operationIds);
    const categories = document.categories.map((item, itemIndex) => itemIndex === index ? archiveStarting(item, command.effectiveFrom) : item);
    const operations = document.operations.map((operation) => operationSet.has(operation.id) ? archiveStarting(operation, command.effectiveFrom) : operation);
    const next = { ...document, categories, operations };
    if (sameDocument(document, next)) throw new DomainError("NO_MAINTENANCE_CHANGE", "category lifecycle is unchanged");
    return proposal(next, [category.id], operationIds, operationIds, command.effectiveFrom);
  }
  if (command.kind === "restore-category") {
    parseIsoDate(command.effectiveFrom);
    const index = categoryIndex(document, command.categoryId);
    const category = document.categories[index]!;
    const cascadeIds = uniqueIds(command.cascadeOperationIds, "INVALID_CASCADE_SELECTION");
    const deselectedIds = uniqueIds(command.deselectedOperationIds ?? [], "INVALID_CASCADE_SELECTION");
    const categoryOperationIds = new Set(document.operations.filter((operation) => operation.categoryId === category.id).map((operation) => operation.id));
    if ([...cascadeIds, ...deselectedIds].some((id) => !categoryOperationIds.has(id)) || deselectedIds.some((id) => !cascadeIds.includes(id))) {
      throw new DomainError("INVALID_CASCADE_SELECTION", "restore selection must contain category descendants archived by the cascade");
    }
    const selectedIds = selectCategoryRestoreOperationIds(cascadeIds, deselectedIds);
    const selected = new Set(selectedIds);
    const categories = document.categories.map((item, itemIndex) => itemIndex === index ? restoreStarting(item, command.effectiveFrom) : item);
    const operations = document.operations.map((operation) => selected.has(operation.id) ? restoreStarting(operation, command.effectiveFrom) : operation);
    const next = { ...document, categories, operations };
    if (sameDocument(document, next)) throw new DomainError("NO_MAINTENANCE_CHANGE", "category lifecycle is unchanged");
    return proposal(next, [category.id], selectedIds, selectedIds, command.effectiveFrom);
  }
  if (command.kind === "delete-operation") {
    const index = operationIndex(document, command.operationId);
    const operation = document.operations[index]!;
    const removedEntries = document.entries.filter((entry) => entry.operationId === operation.id).length;
    const next = {
      ...document,
      categories: document.categories.map((category) => ({ ...category, operationOrder: category.operationOrder.filter((id) => id !== operation.id) })),
      operations: document.operations.filter((item) => item.id !== operation.id),
      entries: document.entries.filter((entry) => entry.operationId !== operation.id),
    };
    return proposal(next, [operation.categoryId], [operation.id], [operation.id], undefined, operation.name, 0, 1, removedEntries);
  }
  if (command.kind !== "delete-category") {
    throw new DomainError("INVALID_MAINTENANCE_COMMAND", "Unsupported owner maintenance command");
  }
  const index = categoryIndex(document, command.categoryId);
  const category = document.categories[index]!;
  const operationIds = document.operations.filter((operation) => operation.categoryId === category.id).map((operation) => operation.id);
  const operationSet = new Set(operationIds);
  const removedEntries = document.entries.filter((entry) => operationSet.has(entry.operationId)).length;
  const next = {
    ...document,
    categories: document.categories.filter((item) => item.id !== category.id).map((item) => ({ ...item, operationOrder: item.operationOrder.filter((id) => !operationSet.has(id)) })),
    operations: document.operations.filter((operation) => !operationSet.has(operation.id)),
    entries: document.entries.filter((entry) => !operationSet.has(entry.operationId)),
  };
  return proposal(next, [category.id], operationIds, operationIds, undefined, category.name, 1, operationIds.length, removedEntries);
}

function proposal(
  document: MatrixDocument,
  affectedCategoryIds: string[],
  affectedOperationIds: string[],
  affectedEntryOperationIds: string[],
  effectiveFrom?: IsoDate,
  deleteName?: string,
  removedCategories = 0,
  removedOperations = 0,
  removedEntries = 0,
): Proposal {
  return {
    document,
    affectedCategoryIds,
    affectedOperationIds,
    affectedEntryOperationIds,
    ...(effectiveFrom === undefined ? {} : { effectiveFrom }),
    ...(deleteName === undefined ? {} : { deleteName }),
    removedCategories,
    removedOperations,
    removedEntries,
  };
}

function categoryDocument(document: MatrixDocument, categoryId: string): MatrixDocument {
  const category = document.categories.find((item) => item.id === categoryId);
  const operations = document.operations.filter((operation) => operation.categoryId === categoryId);
  const operationIds = new Set(operations.map((operation) => operation.id));
  return {
    ...document,
    categories: category ? [{ ...category, operationOrder: category.operationOrder.filter((id) => operationIds.has(id)) }] : [],
    operations,
    entries: document.entries.filter((entry) => operationIds.has(entry.operationId)),
  };
}

export function previewOwnerMaintenance(
  document: MatrixDocument,
  command: OwnerMaintenanceCommand,
  through: IsoDate,
  accumulationAnchor?: IsoDate | null,
): OwnerMaintenancePreview {
  parseIsoDate(through);
  const proposed = proposeMaintenance(document, command);
  const affectedCategoryIds = [...new Set(proposed.affectedCategoryIds)];
  const affectedOperationIds = [...new Set(proposed.affectedOperationIds)];
  const entryIds = new Set(proposed.affectedEntryOperationIds);
  const affectedEntries = document.entries.filter((entry) =>
    entryIds.has(entry.operationId) && (proposed.effectiveFrom === undefined || entry.date >= proposed.effectiveFrom)
  ).length;
  const impactOptions = {
    ...(proposed.effectiveFrom ? { from: proposed.effectiveFrom } : {}),
    ...(accumulationAnchor === undefined ? {} : { anchorBefore: accumulationAnchor, anchorAfter: accumulationAnchor }),
  };
  const impact = previewHistoricalImpact(document, proposed.document, through, impactOptions);
  const categoryImpacts = affectedCategoryIds.map((categoryId) => ({
    categoryId,
    impact: previewHistoricalImpact(
      categoryDocument(document, categoryId),
      categoryDocument(proposed.document, categoryId),
      through,
      impactOptions,
    ),
  }));
  return {
    command,
    through,
    affectedCategoryIds,
    affectedOperationIds,
    counts: {
      affectedCategories: affectedCategoryIds.length,
      affectedOperations: affectedOperationIds.length,
      affectedEntries,
      removedCategories: proposed.removedCategories,
      removedOperations: proposed.removedOperations,
      removedEntries: proposed.removedEntries,
    },
    impact,
    categoryImpacts,
    beforeFingerprint: maintenanceDocumentFingerprint(document),
    afterFingerprint: maintenanceDocumentFingerprint(proposed.document),
    deleteSafeguard: proposed.deleteName === undefined ? null : {
      backupRequired: true,
      confirmationText: proposed.deleteName,
    },
  };
}

export function applyOwnerMaintenance(
  document: MatrixDocument,
  preview: OwnerMaintenancePreview,
  deleteAuthorization?: DeleteAuthorization,
): MatrixDocument {
  const currentFingerprint = maintenanceDocumentFingerprint(document);
  if (currentFingerprint === preview.afterFingerprint) return document;
  if (currentFingerprint !== preview.beforeFingerprint) {
    throw new DomainError("MAINTENANCE_CONFLICT", "owner document changed after preview");
  }
  if (preview.deleteSafeguard && (
    deleteAuthorization?.backupDownloaded !== true ||
    deleteAuthorization.confirmationText !== preview.deleteSafeguard.confirmationText
  )) throw new DomainError("DELETE_SAFEGUARD_REQUIRED", "download a backup and type the exact item name");
  const proposed = proposeMaintenance(document, preview.command).document;
  if (maintenanceDocumentFingerprint(proposed) !== preview.afterFingerprint) {
    throw new DomainError("INVALID_MAINTENANCE_PREVIEW", "maintenance preview does not match the requested change");
  }
  return proposed;
}
